import React, { useEffect, useState } from 'react';
import * as yup from 'yup';
import OverlayLoading from '../../../../../Components/OverlayLoading';
import useNCZICommandProcess from '../../../Hooks/useNCZICommandProcess';
import NCZICommandResult from '../../NCZICommandResult/NCZICommandResult';
import Form from '../../../../../Components/Form/Form';
import InputField from '../../../../../Components/Form/components/InputField';
import SubmitButton from '../../../../../Components/Button/SubmitButton';
import withForm from '../../../../../Components/Form/withForm';
import NCZIReferenceSelectField from '../../ReferenceSelect/NCZIReferenceSelectField';
import FormMultiplier from '../../../../../Components/Form/FormMultiplier';
import NCZIReferenceSearchSelectField from '../../ReferenceSearchSelect/NCZIReferenceSearchSelectField';
import CheckboxField from '../../../../../Components/Form/components/CheckboxField';
import FormControl from '../../../../../Components/Form/FormControl';
import TimePicker from '../../../../../Components/Base/TimePicker/TimePicker';
import DatePicker from '../../../../../Components/Base/DatePicker/DatePicker';
import moment, { Moment } from 'moment-timezone';
import { getDate } from '../../../../../Helpers/date.helper';
import {
	NCZIReferenceSelectItem,
	NCZIReferenceValidator,
} from '../../ReferenceSearchSelect/NCZIReferenceSearchSelect';
import { WritePatientExaminationResult } from '../../../Commands/WritePatientExaminationResult';
import FormCondition from '../../../../../Components/Form/FormCondition';
import SelectField from '../../../../../Components/Form/components/SelectField';
import Text from '../../../../../Components/Base/Text/Text';
import RemoveButton from '../../../../../Components/Button/RemoveButton';
import FormContainer from '../../../../../Components/Form/FormContainer';
import TextAreaField from '../../../../../Components/Form/components/TextAreaField';
import InputWrapper from '../../../../../Components/Form/components/InputWrapper/InputWrapper';
import FormTitle from '../../../../../Components/Form/FormTitle';
import { NCZI_OID } from '../../../Enums/ncziOid.enum';
import { useLoadProfile } from '../../../../../Components/PatientProfile/PatientProfileDataLoader';
import { usePatientTreatmentContext } from '../../../../Patient/Components/PatientTreatment/ContextProvider';
import { NCZIEntry, NCZIReference } from '../../../Interfaces/ncziTypes';
import NZCIListItem from '../../Base/NZCIListItem';
import useTranslation from '../../../../../Hooks/useTranslation';

//@ts-ignore
import styles from './ElectronicExaminationCreateForm.module.scss';

export interface ElectronicExaminationCreateFormValues {
	examinationDate: Moment;
	examinationTime: Moment;
	dispensary: boolean;
	// uncertaintyExpressed: boolean
	nonContractualHealthProfessional: boolean;
	examinationTitle: string;
	recommendation: string;
	anamnesis: string;
	examinationUrgency: NCZIReferenceSelectItem;
	descriptionText: string;
	internalNote: string;
	medication: string;
	expectedDateOfBirth: Moment | undefined;
	useImplantedMedicalDevice: boolean;
	implantedMedicalDevice: {
		description: string;
		type: NCZIReferenceSelectItem;
	};
	useAllergen: boolean;
	allergen: {
		substance: NCZIReferenceSelectItem;
		medicinal: NCZIReferenceSelectItem;
	};
	diagnosis: {
		diagnosis: NCZIReferenceSelectItem;
		isMain: boolean;
		specification: string;
		lifeAffecting: boolean;
	}[];
	useRecommendationForExamination: boolean;
	recommendationForExamination: {
		type: NCZIReferenceSelectItem;
		professionalFocus: NCZIReferenceSelectItem;
		examinedBodyPart: NCZIReferenceSelectItem | undefined;
		modalityOfImagingExamination: NCZIReferenceSelectItem | undefined;
		requiredExamination: string;
		diagnosis: {
			diagnosis: NCZIReferenceSelectItem;
		}[];
		urgency: NCZIReferenceSelectItem;
		patientInfo: string | undefined;
		sedationNeed: boolean;
		extract: {
			item: string;
			type: {
				value: string;
				label: string;
			};
		}[];
	};
}

export const TYPE_STANDARD = 'standard';
export const TYPE_ZZS = 'zzs';

interface Props {
	onClose?: (needReload: boolean) => void;
	patientIdentifier: string;
	takeOverExamination?: NCZIEntry;
	type?: string;
}

const ElectronicExaminationCreateForm: React.FC<Props> = props => {
	const {
		onClose,
		patientIdentifier,
		takeOverExamination,
		type = TYPE_STANDARD,
	} = props;
	const [defaultValues, setDefaultValues] = useState({});
	const [isLoading, setIsLoading] = useState(true);
	const { patientId } = usePatientTreatmentContext();
	const { user } = useLoadProfile({
		userId: patientId,
		loadVerifyInformation: false,
		loadUserRelations: false,
		loadHealthCard: false,
	});
	const { processingCommandId, processCommand, isProcessing } =
		useNCZICommandProcess({
			onFinish: () => onClose?.(true),
		});
	const { t } = useTranslation();
	const takeOverData = takeOverExamination?.structured || undefined;
	const takeOverInfo =
		takeOverData?.examination?.recommendationForExamination || undefined;

	const handleSubmit = async (
		values: ElectronicExaminationCreateFormValues,
	) => {
		const examinationTime = values.examinationDate;
		examinationTime.set({
			hour: values.examinationTime.get('hour'),
			minute: values.examinationTime.get('minute'),
			second: values.examinationTime.get('second'),
		});

		const rfe = values.recommendationForExamination;

		const finalValues = {
			createdAt: moment().format(),
			examinationTime: examinationTime.format(),
			dispensary: values.dispensary,
			uncertaintyExpressed: false,
			nonContractualHealthProfessional: values.nonContractualHealthProfessional,
			recommendation: values.recommendation || undefined,
			anamnesis: values.anamnesis || undefined,
			examinationTitle: values.examinationTitle || undefined,
			internalNote: values.internalNote || undefined,
			medication: values.medication || undefined,
			expectedDateOfBirth:
				values.expectedDateOfBirth?.format('YYYY-MM-DD') || undefined,
			allergen: values.useAllergen
				? {
						substance: values.allergen?.substance?.value,
						medicinal: values.allergen?.medicinal?.value,
				  }
				: undefined,
			implantedMedicalDevice: values.useImplantedMedicalDevice
				? {
						description: values.implantedMedicalDevice?.description,
						type: values.implantedMedicalDevice?.type?.value,
				  }
				: undefined,
			requestTicketOnly: false,
			descriptionText: values.descriptionText,
			versionStatus: 'VER01',
			sensitivity: 3,
			examinationUrgency: values.examinationUrgency.value,
			sentAt: takeOverData?.examination?.createdAt || undefined,
			sender: takeOverData?.examination?.sender || undefined,
			requestForExamination: takeOverInfo?.rcId || undefined,
			diagnosisAtSent: (takeOverInfo?.diagnosis || []).length
				? takeOverInfo?.diagnosis.map((diag: NCZIReference) => {
						return {
							diagnosis: diag,
						};
				  })
				: undefined,
			diagnosis: values.diagnosis.map(diagnosisItem => {
				return {
					diagnosis: diagnosisItem.diagnosis.value,
					isMain: diagnosisItem.isMain,
					lifeAffecting: diagnosisItem.lifeAffecting,
					specification: diagnosisItem.specification || undefined,
				};
			}),
			recommendationForExamination: values.useRecommendationForExamination
				? {
						type: rfe.type.value,
						professionalFocus: rfe.professionalFocus.value,
						examinedBodyPart: rfe.examinedBodyPart?.value || undefined,
						modalityOfImagingExamination:
							rfe.modalityOfImagingExamination?.value || undefined,
						requiredExamination: rfe.requiredExamination,
						diagnosis: rfe.diagnosis.map(
							diagnosisItem => diagnosisItem.diagnosis.value,
						),
						urgency: rfe.urgency.value,
						patientInfo: rfe.patientInfo || undefined,
						sedationNeed: rfe.sedationNeed,
						extract: rfe.extract.length
							? {
									medicalRecords: rfe.extract.map(extractItem => ({
										code: extractItem.item,
										scheme: {
											oid: extractItem.type.value || '',
										},
										valid: undefined,
									})),
							  }
							: undefined,
				  }
				: undefined,
		};

		const writeCommand = WritePatientExaminationResult({
			patientCode: patientIdentifier,
			type: 'ZoOV',
			examination: finalValues,
		});

		await processCommand(writeCommand);
	};

	const validationSchema = yup.object({
		dispensary: yup.boolean(),
		// uncertaintyExpressed: yup.boolean(),
		nonContractualHealthProfessional: yup.boolean(),
		recommendation: yup
			.string()
			.max(2000, 'nczi.validation.max2000')
			.nullable(),
		anamnesis: yup.string().max(6000, 'nczi.validation.max6000').nullable(),
		examinationTitle: yup
			.string()
			.max(512, 'nczi.validation.max512')
			.required('nczi.examination.create.examinationTitle'),
		descriptionText: yup
			.string()
			.max(85000, 'nczi.validation.max85000')
			.required(
				type === TYPE_STANDARD
					? 'nczi.examination.create.descriptionText'
					: 'nczi.examination.create.conclusion',
			),
		internalNote: yup.string().max(6000, 'nczi.validation.max6000'),
		medication: yup.string().max(5000, 'nczi.validation.max5000'),
		allergen: yup.mixed().when('useAllergen', {
			is: true,
			then: yup.object({
				substance: NCZIReferenceValidator(
					'nczi.examination.create.allergen.substance',
				),
				medicinal: NCZIReferenceValidator(
					'nczi.examination.create.allergen.medicinal',
				),
			}),
		}),
		implantedMedicalDevice: yup.mixed().when('useImplantedMedicalDevice', {
			is: true,
			then: yup.object({
				description: yup
					.string()
					.max(512, 'nczi.validation.512')
					.required(
						'nczi.examination.create.implantedMedicalDevice.description',
					),
				type: NCZIReferenceValidator(
					'nczi.examination.create.implantedMedicalDevice.type',
				),
			}),
		}),
		examinationUrgency: NCZIReferenceValidator(
			'nczi.examination.create.examinationUrgency',
		),
		diagnosis: yup
			.array()
			.of(
				yup.object({
					diagnosis: NCZIReferenceValidator(
						'nczi.examination.create.diagnosis.diagnosis',
					),
					specification: yup.string().max(2000, 'nczi.validation.max2000'),
					isMain: yup.boolean(),
					lifeAffecting: yup.boolean(),
				}),
			)
			.min(1)
			.test(
				'contains-main-diagnosis',
				'nczi.examination.create.diagnosis.needOneMain',
				(value, context) => {
					return (value || []).filter(item => item.isMain).length === 1;
				},
			),
		useRecommendationForExamination: yup.boolean(),
		recommendationForExamination: yup
			.mixed()
			.when('useRecommendationForExamination', {
				is: true,
				then: yup.object({
					type: NCZIReferenceValidator(
						'nczi.examination.create.recommendationForExamination.type',
					),
					professionalFocus: NCZIReferenceValidator(
						'nczi.examination.create.recommendationForExamination.professionalFocus',
					),
					examinedBodyPart: NCZIReferenceValidator(
						'nczi.examination.create.recommendationForExamination.examinedBodyPart',
					)
						.nullable()
						.default(null),
					modalityOfImagingExamination: NCZIReferenceValidator(
						'nczi.examination.create.recommendationForExamination.modalityOfImagingExamination',
					)
						.nullable()
						.default(null),
					requiredExamination: yup
						.string()
						.max(6000, 'nczi.validation.max6000')
						.required(
							'nczi.examination.create.recommendationForExamination.requiredExamination',
						),
					diagnosis: yup
						.array()
						.of(
							yup.object({
								diagnosis: NCZIReferenceValidator(
									'nczi.examination.create.recommendationForExamination.diagnosis',
								),
							}),
						)
						.min(1),
					urgency: NCZIReferenceValidator(
						'nczi.examination.create.recommendationForExamination.urgency',
					),
					patientInfo: yup.string().max(2000, 'nczi.validation.max2000'),
					sedationNeed: yup.boolean(),
					extract: yup
						.array()
						.ensure()
						.of(
							yup.object({
								item: yup
									.string()
									.required(
										'nczi.examination.create.recommendationForExamination.extract.item',
									),
								type: yup.object({
									label: yup.string(),
									value: yup
										.string()
										.required(
											'nczi.examination.create.recommendationForExamination.extract.type',
										),
								}),
							}),
						),
				}),
			}),
	});

	useEffect(() => {
		const now = getDate();
		setDefaultValues({
			examinationTitle:
				type === TYPE_STANDARD
					? 'Záznam z vyšetrenia'
					: 'Záznam z vyšetrenia (draft)',
			examinationDate: now,
			examinationTime: now,
			diagnosis: [
				{
					isMain: true,
				},
			],
			recommendationForExamination: {
				diagnosis: [{}],
			},
		});
		if (user) {
			setIsLoading(false);
		}
	}, [user]);

	if (isProcessing && processingCommandId) {
		return <NCZICommandResult commandId={processingCommandId} />;
	}

	console.log({ takeOverData, takeOverInfo });

	return (
		<div>
			{isLoading && <OverlayLoading />}
			{!isLoading && (
				<Form defaultValues={defaultValues} schema={validationSchema}>
					{takeOverInfo && (
						<>
							<Text
								tx={
									'nczi.examination.create.recommendationForExamination.title'
								}
							/>
							<NZCIListItem
								title={
									'nczi.examination.create.recommendationForExamination.rcId'
								}
								value={takeOverInfo.rcId.code}
							/>
							<NZCIListItem
								title={
									'nczi.examination.create.recommendationForExamination.type'
								}
								value={takeOverInfo.type?.displayName || '-'}
							/>
							<NZCIListItem
								title={
									'nczi.examination.create.recommendationForExamination.professionalFocus'
								}
								value={takeOverInfo.professionalFocus?.displayName || '-'}
							/>
							<NZCIListItem
								title={
									'nczi.examination.create.recommendationForExamination.examinedBodyPart'
								}
								value={takeOverInfo.examinedBodyPart?.displayName || '-'}
							/>
							<NZCIListItem
								title={
									'nczi.examination.create.recommendationForExamination.modalityOfImagingExamination'
								}
								value={
									takeOverInfo.modalityOfImagingExamination?.displayName || '-'
								}
							/>
							<NZCIListItem
								title={
									'nczi.examination.create.recommendationForExamination.urgency'
								}
								value={takeOverInfo.urgency?.displayName || '-'}
							/>
							{(takeOverInfo.diagnosis || []).map(
								(diag: NCZIReference, index: number) => {
									return (
										<NZCIListItem
											key={index}
											title={
												'nczi.examination.create.recommendationForExamination.diagnosis'
											}
											value={diag.displayName}
										/>
									);
								},
							)}
							<NZCIListItem
								title={
									'nczi.examination.create.recommendationForExamination.sedationNeed'
								}
								value={takeOverInfo.sedationNeed ? t('base.yes') : t('base.no')}
							/>
							<NZCIListItem
								title={
									'nczi.examination.create.recommendationForExamination.requiredExamination'
								}
								value={takeOverInfo.requiredExamination || '-'}
							/>
							<NZCIListItem
								title={
									'nczi.examination.create.recommendationForExamination.patientInfo'
								}
								value={takeOverInfo.patientInfo || '-'}
							/>
							<br />
							{/*label='nczi.examination.create.recommendationForExamination.diagnosis'*/}
							{/*name='diagnosis'*/}
						</>
					)}
					<div className={'row'}>
						<div className={'col-6'}>
							<FormControl
								label='nczi.examination.create.examinationDate'
								name='examinationDate'
								isRequired>
								<DatePicker />
							</FormControl>
						</div>
						<div className={'col-6'}>
							<FormControl
								label='nczi.examination.create.examinationTime'
								name='examinationTime'
								isRequired>
								<TimePicker />
							</FormControl>
						</div>
					</div>
					<InputWrapper>
						<InputField
							label='nczi.examination.create.examinationTitle'
							name='examinationTitle'
							isRequired
							charactersLimit={512}
						/>
					</InputWrapper>
					<InputWrapper>
						<CheckboxField
							tx='nczi.examination.create.dispensary'
							name='dispensary'
							isRequired
							firstUpper
						/>
					</InputWrapper>
					<InputWrapper>
						<CheckboxField
							tx='nczi.examination.create.nonContractualHealthProfessional'
							name='nonContractualHealthProfessional'
							isRequired
							firstUpper
						/>
					</InputWrapper>
					<InputWrapper>
						<NCZIReferenceSelectField
							label='nczi.examination.create.examinationUrgency'
							name='examinationUrgency'
							oid={NCZI_OID.EXAMINATION_URGENCY}
							isRequired
						/>
					</InputWrapper>
					<InputWrapper>
						<TextAreaField
							label='nczi.examination.create.anamnesis'
							name='anamnesis'
							charactersLimit={6000}
						/>
					</InputWrapper>
					<InputWrapper>
						<TextAreaField
							label={
								type === TYPE_STANDARD
									? 'nczi.examination.create.descriptionText'
									: 'nczi.examination.create.conclusion'
							}
							name='descriptionText'
							isRequired
							charactersLimit={85000}
						/>
					</InputWrapper>
					<InputWrapper>
						<TextAreaField
							label='nczi.examination.create.recommendation'
							name='recommendation'
							isRequired
							charactersLimit={2000}
						/>
					</InputWrapper>
					<InputWrapper>
						<TextAreaField
							label='nczi.examination.create.internalNote'
							name='internalNote'
							isRequired
							charactersLimit={6000}
						/>
					</InputWrapper>
					<InputWrapper>
						<TextAreaField
							label='nczi.examination.create.medication'
							name='medication'
							isRequired
							charactersLimit={5000}
						/>
					</InputWrapper>
					{user?.profile?.gender === 'female' && (
						<FormControl
							label='nczi.examination.create.expectedDateOfBirth'
							name='expectedDateOfBirth'
							isRequired>
							<DatePicker />
						</FormControl>
					)}
					<InputWrapper>
						<CheckboxField
							tx='nczi.examination.create.useAllergen'
							name='useAllergen'
							isRequired
							firstUpper
						/>
					</InputWrapper>
					<FormCondition controlName='useAllergen' equal={true}>
						<FormContainer name={'allergen'} settings={{ showLine: true }}>
							<InputWrapper>
								<NCZIReferenceSearchSelectField
									label='nczi.examination.create.allergen.substance'
									name='substance'
									oid={NCZI_OID.ALLERGENS}
									isRequired
								/>
							</InputWrapper>
							<InputWrapper>
								<NCZIReferenceSearchSelectField
									label='nczi.examination.create.allergen.medicinal'
									name='medicinal'
									oid={NCZI_OID.ATC_CLASSIFICATION_SYSTEM}
									isRequired
								/>
							</InputWrapper>
						</FormContainer>
					</FormCondition>
					<InputWrapper>
						<CheckboxField
							tx='nczi.examination.create.useImplantedMedicalDevice'
							name='useImplantedMedicalDevice'
							isRequired
							firstUpper
						/>
					</InputWrapper>
					<FormCondition controlName='useImplantedMedicalDevice' equal={true}>
						<FormContainer
							name={'implantedMedicalDevice'}
							settings={{ showLine: true }}>
							<InputWrapper>
								<InputField
									label='nczi.examination.create.implantedMedicalDevice.description'
									name='description'
									isRequired
									charactersLimit={512}
								/>
							</InputWrapper>
							<InputWrapper>
								<NCZIReferenceSearchSelectField
									label='nczi.examination.create.implantedMedicalDevice.type'
									name='type'
									oid={NCZI_OID.IMPLANTED_MEDICAL_DEVICES}
									isRequired
								/>
							</InputWrapper>
						</FormContainer>
					</FormCondition>
					<FormTitle
						tx='nczi.examination.create.diagnosticConclusion'
						type='default'
					/>
					<FormMultiplier
						name='diagnosis'
						addButtonTitle={'nczi.examination.create.diagnosis.add'}>
						{({ remove, index, itemsCount }) => (
							<FormContainer settings={{ showLine: true }}>
								<InputWrapper>
									<NCZIReferenceSearchSelectField
										label='nczi.examination.create.diagnosis.diagnosis'
										name='diagnosis'
										oid={NCZI_OID.DIAGNOSIS}
										showCode
										isRequired
									/>
								</InputWrapper>
								<InputWrapper>
									<InputField
										label='nczi.examination.create.diagnosis.specification'
										name='specification'
										isRequired
										charactersLimit={2000}
									/>
								</InputWrapper>
								<InputWrapper>
									<CheckboxField
										tx='nczi.examination.create.diagnosis.isMain'
										name='isMain'
										isRequired
										firstUpper
									/>
								</InputWrapper>
								<InputWrapper>
									<CheckboxField
										tx='nczi.examination.create.diagnosis.lifeAffecting'
										name='lifeAffecting'
										isRequired
										firstUpper
									/>
								</InputWrapper>
								{itemsCount > 1 && (
									<div className={styles.removeItemWrapper}>
										{index !== 0 && (
											<RemoveButton
												onClick={() => {
													remove(index);
												}}
											/>
										)}
									</div>
								)}
							</FormContainer>
						)}
					</FormMultiplier>
					<CheckboxField
						tx='nczi.examination.create.recommendationForExamination.title'
						name='useRecommendationForExamination'
						isRequired
						firstUpper
					/>
					<FormCondition
						controlName='useRecommendationForExamination'
						equal={true}>
						<FormContainer
							name={'recommendationForExamination'}
							settings={{ showLine: true }}>
							<InputWrapper>
								<NCZIReferenceSelectField
									label='nczi.examination.create.recommendationForExamination.type'
									name='type'
									oid={NCZI_OID.RECOMMENDATION_FOR_EXAMINATION}
									isRequired
								/>
							</InputWrapper>
							<InputWrapper>
								<NCZIReferenceSearchSelectField
									label='nczi.examination.create.recommendationForExamination.professionalFocus'
									name='professionalFocus'
									oid={NCZI_OID.PROFESSIONAL_FOCUS}
									isRequired
								/>
							</InputWrapper>
							<InputWrapper>
								<NCZIReferenceSearchSelectField
									label='nczi.examination.create.recommendationForExamination.examinedBodyPart'
									name='examinedBodyPart'
									oid={NCZI_OID.EXAMINED_BODY_PART}
									isRequired
								/>
							</InputWrapper>
							<InputWrapper>
								<NCZIReferenceSelectField
									label='nczi.examination.create.recommendationForExamination.modalityOfImagingExamination'
									name='modalityOfImagingExamination'
									oid={NCZI_OID.MODALITY_OF_IMAGING_EXAMINATION}
									isRequired
								/>
							</InputWrapper>
							<InputWrapper>
								<InputField
									label='nczi.examination.create.recommendationForExamination.requiredExamination'
									name='requiredExamination'
									isRequired
									charactersLimit={6000}
								/>
							</InputWrapper>
							<FormMultiplier
								name='diagnosis'
								addButtonTitle={'nczi.examination.create.diagnosis.add'}>
								{({ remove, index, itemsCount }) => (
									<FormContainer settings={{ showLine: true }}>
										<InputWrapper>
											<NCZIReferenceSearchSelectField
												label='nczi.examination.create.recommendationForExamination.diagnosis'
												name='diagnosis'
												oid='1.3.158.00165387.100.10.25'
												isRequired
											/>
										</InputWrapper>
										{itemsCount > 1 && (
											<div className={styles.removeItemWrapper}>
												{index !== 0 && (
													<RemoveButton
														onClick={() => {
															remove(index);
														}}
													/>
												)}
											</div>
										)}
									</FormContainer>
								)}
							</FormMultiplier>
							<InputWrapper>
								<NCZIReferenceSelectField
									label='nczi.examination.create.recommendationForExamination.urgency'
									name='urgency'
									oid={NCZI_OID.EXAMINATION_URGENCY}
									isRequired
								/>
							</InputWrapper>
							<InputWrapper>
								<InputField
									label='nczi.examination.create.recommendationForExamination.patientInfo'
									name='patientInfo'
									isRequired
									charactersLimit={2000}
								/>
							</InputWrapper>
							<Text
								tx={
									'nczi.examination.create.recommendationForExamination.extract.title'
								}
							/>
							<FormMultiplier
								name='extract'
								addButtonTitle={
									'nczi.examination.create.recommendationForExamination.extract.add'
								}>
								{({ remove, index, itemsCount }) => (
									<FormContainer settings={{ showLine: true }}>
										<div className={'row'}>
											<div className={'col-6'}>
												<InputWrapper>
													<InputField
														label='nczi.examination.create.recommendationForExamination.extract.item'
														name='item'
														isRequired
													/>
												</InputWrapper>
											</div>
											<div className={'col-6'}>
												<InputWrapper>
													<SelectField
														label='nczi.examination.create.recommendationForExamination.extract.type'
														name='type'
														options={[
															{
																value: '1.3.158.00165387.100.60.10',
																label: 'Laboratórny výsledok',
															},
															// {value: '1.3.158.00165387.100.10.35', label: 'Druh očkovaní'}, TODO Ciselnik
															{
																value: '1.3.158.00165387.100.60.80',
																label: 'Záznam o zobrazovacom vyšetrení',
															},
															{
																value: '1.3.158.00165387.100.60.90',
																label: 'Záznam o odbornom vyšetrení',
															},
															{
																value: '1.3.158.00165387.100.60.100',
																label: 'Lekárska prepúštacia správa',
															},
														]}
													/>
												</InputWrapper>
											</div>
										</div>
										{itemsCount > 0 && (
											<div className={styles.removeItemWrapper}>
												<RemoveButton
													onClick={() => {
														remove(index);
													}}
												/>
											</div>
										)}
									</FormContainer>
								)}
							</FormMultiplier>
							<InputWrapper>
								<CheckboxField
									tx='nczi.examination.create.recommendationForExamination.sedationNeed'
									name='sedationNeed'
									isRequired
									firstUpper
								/>
							</InputWrapper>
						</FormContainer>
					</FormCondition>
					<div className='mt-4'>
						<SubmitButton
							title='component.nczi.setup.save'
							onSubmit={handleSubmit}
						/>
					</div>
				</Form>
			)}
		</div>
	);
};

export default withForm(ElectronicExaminationCreateForm);
